import { Grid, Skeleton, Snackbar, Typography } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import FinancialReportService from "../../Services/financialReport.service";

const FinancialReport = () => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const { vertical, horizontal, open } = state;
  const handleClick = (newState) => () => {
    setState({ ...newState, open: true });
  };
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [filteredData, setFilteredData] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
  };

  const handleClose = () => {
    setState({ ...state, open: false });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchFinancialReport = async (start_date, end_date) => {
    try {
      setLoading(true);
      const formattedStartDate = start_date ? formatDate(start_date) : null;
      const formattedEndDate = end_date ? formatDate(end_date) : null;
      const financialReport = await FinancialReportService.getAllReports({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      });

      setFilteredData(financialReport.data.summary);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching maintenance requests:", error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialReport(startDate, endDate);
  }, [startDate, endDate]);

  return (
    <div>
      <Helmet>
        <title>SkyBMS - {t("title.financialReport")}</title>
      </Helmet>

      <>
        <div className="flex justify-between items-center py-7">
          <div className="flex flex-col gap-3">
            <Typography
              variant="h6"
              sx={{ color: "#909aab", alignSelf: "start" }}
            >
              {t("financialReport.reportTitle")}
            </Typography>
          </div>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <div style={{ padding: 16, backgroundColor: "#fff" }}>
              <Typography
                variant="h6"
                sx={{ color: "#909aab", alignSelf: "start" }}
              >
                {t("financialReport.filterReports")}
              </Typography>

              <Grid
                container
                spacing={2}
                marginTop={2}
                justifyContent="center"
                alignItems="center"
              >
                <Grid item xs={12} sm={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <DatePicker
                        label={t("financialReport.from")}
                        value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                        }}
                      />
                      <DatePicker
                        label={t("financialReport.to")}
                        value={endDate}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                        }}
                      />
                    </div>
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </div>
          </Grid>

          <Grid item xs={12} md={8}>
            <div style={{ backgroundColor: "#fff" }}>
              <div>
                <Typography
                  variant="h6"
                  sx={{ color: "#909aab", paddingY: 1, alignSelf: "center" }}
                  align="center"
                >
                  {t("financialReport.reportView")}
                </Typography>
                <Fade
                  in={loading}
                  style={{ transitionDelay: loading ? "100ms" : "0ms" }}
                  unmountOnExit
                >
                  <div style={{ padding: 16 }}>
                    <Skeleton variant="text" width={210} height={30} />
                    <Skeleton variant="text" width={150} height={30} />
                    <Skeleton variant="text" width={180} height={30} />
                    <Skeleton variant="text" width={170} height={30} />
                  </div>
                </Fade>
                <div style={{ height: "400px", paddingLeft: "10px" }}>
                  {!loading &&
                    filteredData &&
                    startDate !== null &&
                    endDate !== null && (
                      <div>
                        <Typography variant="body1">
                          {t("financialReport.reportPeriod")}:{" "}
                          {formatDate(startDate)} {"-"} {formatDate(endDate)}
                        </Typography>
                        <Typography variant="body1">
                          {t("financialReport.totalIncome")}:{" "}
                          {filteredData.grand_total_payment}
                        </Typography>
                        <Typography variant="body1">
                          {t("financialReport.totalExpenses")}:{" "}
                          {filteredData.grandTotalWorkOrderCost}
                        </Typography>
                        <Typography variant="body1">
                          {t("financialReport.net")}: {filteredData.net}
                        </Typography>
                        <Typography variant="body1">
                          {t("financialReport.outstandingPayments")}:{" "}
                          {filteredData.outstandingPayments}
                        </Typography>
                      </div>
                    )}
                  {filteredData &&
                    !loading &&
                    startDate === null &&
                    endDate === null && (
                      <div>
                        <Typography
                          variant="body1"
                          align="center"
                          justifyContent="center"
                          sx={{ color: "#909aab" }}
                        >
                          {t("financialReport.selectReportPeriod")}
                        </Typography>
                      </div>
                    )}
                  {!filteredData &&
                    !loading &&
                    startDate !== null &&
                    endDate !== null && (
                      <div>
                        <Typography variant="body1" align="center">
                          {t("financialReport.noDataFound")}
                        </Typography>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </Grid>
        </Grid>

        <div className="mt-8"></div>
        {successMessage && (
          <Box sx={{ width: 500 }}>
            <Snackbar
              anchorOrigin={{ vertical, horizontal }}
              open={open}
              onClose={handleClose}
              autoHideDuration={2000}
              key={vertical + horizontal}
            >
              <MuiAlert
                onClose={handleClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                {successMessage}
              </MuiAlert>
            </Snackbar>
          </Box>
        )}
        {error && (
          <Box sx={{ width: 500 }}>
            <Snackbar
              anchorOrigin={{ vertical, horizontal }}
              open={open}
              onClose={handleClose}
              autoHideDuration={2000}
              key={vertical + horizontal}
            >
              <MuiAlert
                onClose={handleClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                {error}
              </MuiAlert>
            </Snackbar>
          </Box>
        )}
      </>
      {/* )} */}
    </div>
  );
};

export default FinancialReport;
